<template>
    <VueSelect
        ref="select"
        v-model="selected"
        :placeholder="placeholder"
        :options="ownOptions"
        :is-clearable="clearable && !$attrs.disabled"
        :is-searchable="isSearchable"
        :is-multi="isMulti"
        :is-loading="isLoading"
        :close-on-select="!isMulti"
        class="custom-vselect"
        :get-option-label="option => getOptionLabel(option)"
        :get-option-value="option => getOptionValue(option)"
        @search="search($event)"
    >
        <template #tag="{ option }">
            <div class="multi-value">
                <div class="multi-value-label">
                    {{ getOptionLabel(option) }}
                </div>
                <button
                    class="multi-value-remove"
                    @click.prevent="removeSelectedOption(option)"
                >
                    <i class="fa fa-times" />
                </button>
            </div>
        </template>

        <template #value="{ option }">
            {{ getOptionLabel(option) }}
        </template>

        <template
            v-for="slot in Object.keys($slots)"
            #[slot]="scope"
        >
            <slot
                :name="slot"
                v-bind="scope.option"
            />
        </template>

        <template #no-options>
            {{ isColumnFilter ? $tc('filter.no-results') : $tc('actions.type-to-search') }}
        </template>
    </VueSelect>
</template>

<script>
import Model from '@/models/Model';
import _debounce from 'lodash.debounce';
import VueSelect from 'vue3-select-component';

export default {
    components: {
        VueSelect,
    },

    props: {
        modelValue: [Number, String, Array],
        model: {
            type: [Object, Function],
            required: false,
        },
        /**
         * Function with a `search` parameter that returns a {Promise} to allow for lazy loading of options.
         */
        searchFn: {
            type: Function,
            default: null,
        },
        /**
         * Minimum string length for a search to start. Lowering this number will increase the number of matches.
         */
        minSearchLength: {
            type: Number,
            default: 2,
        },
        options: {
            type: [Array, Object],
            default: () => [],
        },
        isColumnFilter: {
            type: Boolean,
            default: false,
        },
        allowNullValue: {
            type: Boolean,
            default: true,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        configName: {
            type: String,
            default: '',
        },
        isSearchable: {
            type: Boolean,
            default: false,
        },
        isMulti: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: 'name',
        },
        valueKey: {
            type: String,
            default: 'id',
        },
        placeholder: {
            type: String,
            required: true,
        },
    },

    data () {
        return {
            ownOptions: this.parseOptions(this.options),
            isLoading: false,
            selected: [],
        };
    },

    watch: {
        options: {
            handler (newVal) {
                this.ownOptions = this.parseOptions(newVal);
            },
            immediate: true,
        },
        selected: {
            handler () {
                this.$emit('update:modelValue', this.selected);
            },
            deep: true,
        },
        modelValue: {
            handler (val) {
                this.selected = val;
            },
            deep: true,
        },
    },

    created () {
        if (this.model && this.modelValue) {
            if (Array.isArray(this.modelValue) && !this.modelValue.length) {
                return; // Empty array, do nothing
            }

            if (this.modelValue === 'empty-option') {
                this.selected = this.modelValue;
                this.ownOptions = this.parseOptions(this.selected);
                return;
            }

            let b = null;
            if (this.modelValue.length) {
                b = [
                    { whereIn: [this.valueKey, this.modelValue] },
                ];
            } else {
                b = [
                    { where: [this.valueKey, this.modelValue] },
                ];
            }

            this.model.index({
                builder: b,
            }).then((response) => {
                this.ownOptions = this.parseOptions(response.data);
                this.selected = this.modelValue;
            });
        }
    },

    methods: {
        removeSelectedOption (option) {
            // Remove the option from the selected array
            this.selected = this.selected.filter(selectedOption => selectedOption !== option[this.valueKey]);
        },

        search (event) {
            this.searchOptions(event, () => {
            });

            this.$emit('search', event);
        },

        parseOptions (newVal) {
            const options = Array.isArray(newVal)
                ? [...newVal]
                : (newVal && typeof newVal === 'object')
                        ? Object.values(newVal)
                        : [];

            if (this.isColumnFilter && this.allowNullValue) {
                const M = this.model ? this.model : Model;
                options.push(new M({
                    id: 'empty-option',
                    [this.label]: `(${this.$t('defaults.none')})`,
                }));
            }

            return options;
        },

        searchOptions: _debounce(function ($event) {
            if (this.searchFn && $event.length >= this.minSearchLength) {
                this.isLoading = true;
                this.searchFn($event).then((response) => {
                    // Find the options that are already in the selected array
                    // Keep them in the options array so they don't disappear
                    const selectedOptions = this.ownOptions.filter((option) => {
                        return Array.isArray(this.selected) && this.selected.includes(option[this.valueKey]);
                    });

                    // ownOptions should be an array of the new options and the selected options
                    this.ownOptions = this.parseOptions(response.data).concat(selectedOptions);
                }).finally(() => {
                    this.isLoading = false;
                });
            }

            if (!this.searchFn) {
                // Filter the options based on the search term (fuzzy search)
                const options = this.options.filter((option) => {
                    return option[this.label].toLowerCase().includes($event.toLowerCase());
                });

                this.isLoading = false;
                this.ownOptions = this.parseOptions(options);
            }
        }, 300),

        getOptionLabel (option) {
            if (!option) {
                return '';
            }

            return option[this.label];
        },
        getOptionValue (option) {
            if (!option) {
                return '';
            }

            return option[this.valueKey];
        },
    },
};
</script>

<style lang="scss">
.multi-value {
    display: flex;
    min-width: 0;
    margin: var(--vs-multi-value-margin);
    border-radius: var(--vs-multi-value-border-radius);
    background: var(--vs-multi-value-background-color);
}

.multi-value-label {
    padding: var(--vs-multi-value-label-padding);
    border: var(--vs-multi-value-border);
    border-radius: var(--vs-multi-value-border-radius);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--vs-multi-value-label-font-size);
    font-weight: var(--vs-multi-value-label-font-weight);
    line-height: var(--vs-multi-value-label-line-height);
    color: var(--vs-multi-value-label-text-color);
}

.multi-value-remove {
    border-radius: var(--vs-multi-value-border-radius);
    appearance: none;
    display: flex;
    align-items: center;
    padding: var(--vs-multi-value-delete-padding);
    border: none;
    outline: none;
    cursor: var(--vs-multi-value-xmark-cursor);
    background-color: var(--vs-multi-value-background-color);
}
</style>
